import { AmplitudeClient } from 'amplitude-js'
import * as amplitude from 'amplitude-js'
import { getEnv } from 'utils/env'
import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import AmplitudeError from 'error/amplitudeError'
import AmplitudeTimeoutError from 'error/amplitudeTimeoutError'
import { logWarning } from 'utils/log'
import { includesExactPattern } from 'utils/string'
import { getCookie } from 'utils/cookie'
import { isTenantSmava } from 'utils/url'
import { getEnvMulti } from '../../env'
import * as Package from '../../../../package.json'
import pollard from '../../pollard'
import { isOk } from '../../url/status'
import {
  AmplitudeEvent,
  getScope,
  EventProperty,
  UserProperty
} from './amplitude.interface'

const AMPLITUDE_ID = 'amplitudeId'
const instanceName = 'taurine'
const [apiKey, cookieLifetime, allowList] = getEnvMulti([
  'REACT_APP_AMPLITUDE_ID',
  'REACT_APP_AMPLITUDE_COOKIE_LIFETIME',
  'REACT_APP_AMPLITUDE_ID_ALLOW_LIST'
])

const isSmava = isTenantSmava()
const amplitudeInstance: AmplitudeClient = amplitude.getInstance(instanceName)

// CAT-23 Init Amplitude with AmplitudeID from URL
// as amplitude should be initialised in early phase of application start
// we will directly read apiKey from the query params (reason why rootStore.urlParams are not used)
const urlParams = new URLSearchParams(window.location.search)
const amplitudeId: string | null = urlParams.get(AMPLITUDE_ID)

amplitudeInstance.init(
  amplitudeId && includesExactPattern(allowList, amplitudeId)
    ? amplitudeId
    : apiKey,
  undefined,
  {
    cookieExpiration: +cookieLifetime,
    batchEvents: true,
    eventUploadPeriodMillis: 1000,
    onError: () =>
      logAndTrackError(new Error('Could not initialize Amplitude.')),
    trackingOptions: {
      ip_address: false
    },
    optOut: true
  }
)

amplitudeInstance.clearUserProperties()
amplitudeInstance.setVersionName(Package.version)

const placementId =
  getCookie('placementId') || urlParams.get('placementId') || null

setSegmentAmplitudeUserProperty({
  [UserProperty.Tenant]: getEnv('REACT_APP_TAURINE_TENANT_NAME'),
  [UserProperty.PlacementID]: placementId
})

broadcastToParent({
  eventName: RegisteredEventName.callSegmentIdentify,
  data: {
    [UserProperty.PlacementID]: placementId
  }
})

/**
 * This is needed to recover user properties that were set when Amplitude was disabled.
 * Apparently, the properties get dropped then (won't get added in the first place).
 */
let userProperties: Partial<Record<UserProperty, unknown>> = {}

function setSegmentAmplitudeUserProperty(
  properties: Partial<Record<UserProperty, unknown>>
) {
  broadcastToParent({
    eventName: RegisteredEventName.callSegmentIdentify,
    data: { ...properties, team: 'catapult' }
  })
}

export function setUserProperties(
  properties: Partial<Record<UserProperty, unknown>>
) {
  if (isSmava) {
    setSegmentAmplitudeUserProperty(properties)
  } else {
    amplitudeInstance.setUserProperties(properties)
  }
  userProperties = { ...userProperties, ...properties }
}

export function setAmplitudeOptOut(isOptedOut: boolean) {
  amplitudeInstance.setOptOut(isOptedOut)

  if (!isOptedOut) {
    amplitudeInstance.setUserProperties(userProperties)
  }
}

pollard.listenTo<'setOptOut', { generalOptOut: boolean }>(
  'setOptOut',
  ({ generalOptOut }) => {
    setAmplitudeOptOut(generalOptOut)
  }
)

export function getAmplitudeUserId() {
  return amplitudeInstance.options.userId
}

export function setAmplitudeUserId(userId: string) {
  amplitudeInstance.setUserId(userId)
}

const globalEventProperties: Partial<Record<EventProperty, unknown>> = {}

export function setGlobalEventProperty<T>(property: EventProperty, value: T) {
  globalEventProperties[property] = value
}

export const trackInAmplitude = async <T>(
  event: AmplitudeEvent,
  payload?: T,
  timeout = 5000
) => {
  if (isSmava) {
    broadcastToParent({
      eventName: RegisteredEventName.tracking,
      data: {
        event,
        scope: getScope(event),
        team: 'catapult',
        ...globalEventProperties,
        ...payload
      }
    })
    return Promise.resolve(-1)
  }

  if (amplitudeInstance.options.optOut) {
    logWarning(`[Amplitude] "${event}" not sent because the user opted out..`)
    return Promise.resolve(-1)
  }

  return new Promise((resolve, reject) => {
    const waitId = setTimeout(
      () => reject(new AmplitudeTimeoutError(event, timeout)),
      timeout
    )
    const amplitudeCallback = (
      code: number,
      body: string,
      details?: { reason: string }
    ) => {
      clearTimeout(waitId)

      if (isOk(code)) {
        resolve(code)
        return
      }

      logAndTrackError(
        new AmplitudeError(event, {
          code,
          body,
          reason: details?.reason
        })
      )

      reject(details?.reason)
    }

    amplitudeInstance.logEvent(
      event,
      { scope: getScope(event), ...globalEventProperties, ...payload },
      amplitudeCallback
    )
  }).catch((error) => logAndTrackError(new AmplitudeError(error), 'warning'))
}

interface EventCollector<T> {
  key: string
  items: Array<T>
  timeout: number
}

const eventCollector: Partial<Record<AmplitudeEvent, EventCollector<unknown>>> =
  {}

function getEventCollector<T>(event: AmplitudeEvent, key: string) {
  const collector = eventCollector[event] as EventCollector<T> | undefined
  if (collector) {
    /**
     * If the key was changed,
     * we emit all the items we collected until now (with the old key).
     */
    if (collector.key !== key) {
      try {
        trackInAmplitude(event, {
          [collector.key]: collector.items
        })
      } finally {
        collector.items.length = 0
      }
    }

    return collector
  }

  const newCollector: EventCollector<T> = {
    key,
    items: [],
    timeout: window.setTimeout(async () => {
      clearTimeout(newCollector.timeout)

      try {
        await trackInAmplitude(event, {
          [newCollector.key]: newCollector.items
        })
      } finally {
        delete eventCollector[event]
      }
    }, 1000)
  }

  eventCollector[event] = newCollector

  return newCollector
}

/**
 * Use this method if you want to collect a list of 'events'
 * that are initiated at different places in the app.
 *
 * The list is then tracked as a single Amplitude event.
 */
export const collectListForAmplitude = <T>(
  event: AmplitudeEvent,
  key: string,
  item: T
) => getEventCollector<T>(event, key).items.push(item)
